import api from './api';

export interface ReferralCodeSummary {
    id: string;
    code: string;
    totalEarned: number;
    usageCount: number;
    createdAt: string;
}

export interface ReferralUsageDetail {
    user: { name: string; email: string } | null;
    subscriptionAmount: number;
    commission: number;
    planName: string;
    date: string;
}

export interface ReferralCodeDetail extends ReferralCodeSummary {
    usages: ReferralUsageDetail[];
}

export interface MyCodesResponse {
    codes: ReferralCodeSummary[];
    summary: {
        totalCodes: number;
        totalEarned: number;
    };
}

export type WithdrawalStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
export type PayoutMethod = 'upi' | 'bank_transfer';

export interface WithdrawalSummary {
    totalEarned: number;
    totalWithdrawn: number;
    pendingAmount: number;
    availableBalance: number;
    minWithdrawalAmount: number;
}

export interface EPWithdrawalRequest {
    id: string;
    amount: number;
    status: WithdrawalStatus;
    payoutMethod: PayoutMethod;
    payoutDetails: {
        upiId?: string;
        accountHolderName?: string;
        accountNumber?: string;
        ifscCode?: string;
    };
    note?: string;
    rejectionReason?: string;
    paymentReference?: string;
    processedAt?: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateWithdrawalPayload {
    amount: number;
    payoutMethod: PayoutMethod;
    payoutDetails: {
        upiId?: string;
        accountHolderName?: string;
        accountNumber?: string;
        ifscCode?: string;
    };
    note?: string;
}

export interface PartnerPayoutStatus {
    hasConnectAccount: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    chargesEnabled: boolean;
    currentlyDueCount: number;
    disabledReason?: string | null;
    accountId?: string | null;
}

/** Generate a new referral code (EP only) */
export const generateReferralCode = async (): Promise<{ referralCode: ReferralCodeSummary }> => {
    const response = await api.post('/referral/generate');
    return response.data;
};

/** Get all referral codes for the authenticated EP */
export const getMyCodes = async (): Promise<MyCodesResponse> => {
    const response = await api.get('/referral/my-codes');
    return response.data;
};

/** Get per-usage details for a specific code */
export const getCodeDetails = async (code: string): Promise<ReferralCodeDetail> => {
    const response = await api.get(`/referral/code/${code}`);
    return response.data;
};

/** Get withdrawal summary for authenticated EP */
export const getWithdrawalSummary = async (): Promise<{ summary: WithdrawalSummary }> => {
    const response = await api.get('/referral/withdrawals/summary');
    return response.data;
};

/** Get own withdrawal requests for authenticated EP */
export const getMyWithdrawals = async (): Promise<{ requests: EPWithdrawalRequest[]; summary: WithdrawalSummary }> => {
    const response = await api.get('/referral/withdrawals');
    return response.data;
};

/** Create a new withdrawal request */
export const createWithdrawalRequest = async (
    payload: CreateWithdrawalPayload
): Promise<{
    message: string;
    request: EPWithdrawalRequest;
    summary: WithdrawalSummary;
    stripe?: {
        payoutId: string;
        transferId: string;
        payoutStatus: string;
        currency: string;
    };
}> => {
    const response = await api.post('/referral/withdrawals/request', payload);
    return response.data;
};

/** Cancel a pending withdrawal request */
export const cancelWithdrawal = async (
    requestId: string
): Promise<{ message: string; request: EPWithdrawalRequest; summary: WithdrawalSummary }> => {
    const response = await api.patch(`/referral/withdrawals/${requestId}/cancel`);
    return response.data;
};

/** Get Stripe payout setup status for authenticated EP */
export const getPartnerPayoutStatus = async (): Promise<PartnerPayoutStatus> => {
    const response = await api.get('/referral/withdrawals/payout-status');
    return response.data;
};

/** Create Stripe Connect onboarding link for EP */
export const createPartnerOnboardingLink = async (): Promise<{
    url: string;
    expiresAt: number;
    status: Omit<PartnerPayoutStatus, 'hasConnectAccount'>;
}> => {
    const response = await api.post('/referral/withdrawals/onboarding-link');
    return response.data;
};

/** Validate a referral code (public) */
export const validateReferralCode = async (
    code: string
): Promise<{ valid: boolean; epName?: string; code?: string; discountPercent?: number; type?: 'referral' | 'promo'; planName?: string }> => {
    const response = await api.get(`/referral/validate/${code}`);
    return response.data;
};
